import {Stack, Box, Typography, Skeleton} from "@mui/material"
import {useQuery} from "@tanstack/react-query"
import {type FC} from "react"
import {useTranslation} from "react-i18next"
import {ErrorMessage, TrainingTileItem} from "../../molecules"
import {trainingEndPoints} from "../../../services"
import defaultImage from "../../../assets/images/training-default-img.jpg"

const TrainingTile: FC = () => {
  const {getTrainings} = trainingEndPoints()
  const {t} = useTranslation()

  const {
    data,
    isLoading,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
    refetch,
  } = useQuery({
    queryKey: ["trainings"],
    queryFn: () => getTrainings(2, 0),
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  const loadingData = isLoading || isRefetching || !data
  const hasError = isError || isLoadingError || isRefetchError

  return (
    <Stack
      component="section"
      spacing={1}
      position="relative"
      style={{
        padding: "8px",
        borderRadius: "8px",
        borderStyle: !hasError ? "solid" : "none",
        borderWidth: "1px",
        borderColor: "#cdcdcd",
      }}
    >
      <Box>
        <Typography
          style={{
            textAlign: "start",
            color: "#242d35",
            fontSize: "14px",
            fontWeight: hasError ? "normal" : "bold",
            fontStyle: "normal",
            letterSpacing: 0,
            filter: hasError ? "blur(2px)" : "none",
          }}
        >
          {t("Our training")}
        </Typography>
      </Box>
      {!loadingData
        ? data?.map((item) => (
            <Box
              sx={{
                height: "84px",
                width: "100%",
              }}
            >
              <TrainingTileItem
                id={item.eventID}
                title={item.name}
                imageUrl={item?.imageBannerUrl || defaultImage}
                startDate={item.startDate}
                linkUrl={item.url}
              />
            </Box>
          ))
        : [1, 2].map(() => (
            <Box
              sx={{
                height: "84px",
                width: "100%",
                padding: "0px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-end",
                gap: "4px",
                borderRadius: "4px",
                position: "relative",
                filter: hasError ? "blur(5px)" : "none",
              }}
            >
              <Skeleton
                variant="rectangular"
                animation="wave"
                style={{
                  borderRadius: "4px",
                  height: "84px",
                  width: "100%",
                  backgroundImage:
                    "linear-gradient(to right, #cacdcd 0%, #f5f5f5 100%)",
                }}
              ></Skeleton>
              <Box
                sx={{
                  width: "70%",
                  height: "35px",
                  position: "absolute",
                  bottom: "28px",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  style={{
                    height: "35px",
                    backgroundImage:
                      "linear-gradient(to right, #cacdcd 0%, #f5f5f5 100%)",
                    boxShadow: "0px 2px 2px 0px #f5f5f5",
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: "45%",
                  height: "17px",
                  position: "absolute",
                  bottom: "5px",
                }}
              >
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  style={{
                    height: "17px",
                    backgroundImage:
                      "linear-gradient(to right, #cacdcd 0%, #f5f5f5 100%)",
                    boxShadow: "0px 4px 4px 0px #f5f5f5",
                  }}
                />
              </Box>
            </Box>
          ))}
      {hasError && (
        <Box
          sx={{
            padding: "20px",
            position: "absolute",
            width: "100%",
            zIndex: 2,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            margin: 0,
          }}
        >
          <ErrorMessage
            message={"This content is not available right now"}
            messageStyle={{
              fontSize: "14px",
              color: "#595958",
              fontFamily: "Open Sans",
              fontWeight: "normal",
              fontStyle: "normal",
              letterSpacing: 0,
              textAlign: "center",
            }}
            containerStyle={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              verticalAlign: "middle",
            }}
            buttonContainerStyle={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              verticalAlign: "middle",
            }}
            refetch={refetch}
          />
        </Box>
      )}
    </Stack>
  )
}

TrainingTile.displayName = "TrainingTile"

export default TrainingTile
